import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ILocalization } from 'bernie-l10n';

import { UitkStepInput } from 'uitk-react-step-input';
import { UitkTertiaryButton } from 'uitk-react-button';

import { ChildrenAges } from './children-ages';
import { RoomPickerModel } from 'components/common/room-picker/room-picker-model';

const TertiaryButtonTracked = ClickTracker(UitkTertiaryButton);

interface RoomProps {
  analytics?: AnalyticsStore;
  roomIndex: number;
  headerRef: React.RefObject<HTMLHeadingElement>;
  l10n: ILocalization;
  onChildAgeUpdated: (roomIndex, childIndex, target) => void;
  onRoomAdultsChanged: (room, event) => void;
  onRoomChildrenChanged: (room, event) => void;
  onRemoveRoom: (room) => void;
  room: any;
  selection: RoomPickerModel;
}

export const Room = ({
  analytics,
  roomIndex,
  headerRef,
  l10n,
  onChildAgeUpdated,
  onRoomAdultsChanged,
  onRoomChildrenChanged,
  onRemoveRoom,
  room,
  selection
}: RoomProps) => {
  const { roomCount } = selection;
  const stepInputAnalytics = analytics.setupTracker(':Guest', 'Adjust guest count in change search');

  const handleChildAgeUpdated = (childIndex, target) => onChildAgeUpdated(roomIndex, childIndex, target);
  const adultsChanged = event => onRoomAdultsChanged(room, event);
  const childrenChanged = event => onRoomChildrenChanged(room, event);
  const roomRemoved = () => onRemoveRoom(room);
  const roomIndexHeader = roomIndex + 1;
  const isLastRoom = selection.roomCount === roomIndexHeader;

  return (
    <div key={`room-${roomCount}-${roomIndex}`} className="room-picker-room all-y-padding-four">
      <h3
        tabIndex={0}
        className="room-header uitk-type-200 uitk-type-medium"
        ref={isLastRoom ? headerRef : null}
        aria-label={l10n.formatText('hotels.roomPicker.room.a11yLabelRoom', roomIndexHeader, roomCount)}
      >
        <span aria-hidden="true">{l10n.formatText('hotels.roomPicker.room.title', roomIndexHeader)}</span>
      </h3>
      <UitkStepInput
        title={l10n.formatText('hotels.roomPicker.adults.text')}
        increaseText={l10n.formatText('hotels.roomPicker.increaseAdults.text', roomCount)}
        decreaseText={l10n.formatText('hotels.roomPicker.decreaseAdults.text', roomCount)}
        max={14}
        min={1}
        value={room.adults}
        onChange={adultsChanged}
        analytics={stepInputAnalytics}
        id={`room-${roomCount}-${roomIndex}-adults`}
      />
      <UitkStepInput
        title={l10n.formatText('hotels.roomPicker.children.text')}
        increaseText={l10n.formatText('hotels.roomPicker.increaseChildren.text', roomCount)}
        decreaseText={l10n.formatText('hotels.roomPicker.decreaseChildren.text', roomCount)}
        caption={l10n.formatText('hotels.roomPicker.children.caption')}
        max={6}
        min={0}
        value={room.children.length}
        onChange={childrenChanged}
        analytics={stepInputAnalytics}
        id={`room-${roomCount}-${roomIndex}-children`}
      />
      {room.children.length > 0 && (
        <div className="uitk-grid all-y-gutter-two all-x-gutter-two">
          {room.children.map((childAge, childIndex) => (
            <ChildrenAges
              childAge={childAge}
              childIndex={childIndex}
              key={childIndex}
              l10n={l10n}
              onChildAgeUpdated={handleChildAgeUpdated}
              room={room}
              roomIndex={roomIndex}
              selectLabel={l10n.formatText('hotels.roomPicker.childAgeInput.label', childIndex + 1)}
            />
          ))}
        </div>
      )}
      {roomIndex !== 0 && (
        <div className="uitk-type-right all-t-padding-four">
          <TertiaryButtonTracked
            data-stid="remove-room"
            linkName="Remove room"
            onClick={roomRemoved}
            rfrr=":RemoveRoom"
            text={l10n.formatText('hotels.roomPicker.removeRoomButton.label')}
            aria-label={l10n.formatText('hotels.roomPicker.removeRoomButton.a11yLabel', roomIndexHeader, roomCount)}
          />
        </div>
      )}
    </div>
  );
};
